import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Lock, Check, ChevronRight, ArrowLeft, Save, Clock, History, ShieldCheck } from "lucide-react";
import { API, useRoleHome } from "@/contexts/AuthContext";
import Header from "@/components/shared/Header";
import MOTAwarenessPanel from "@/components/intake/MOTAwarenessPanel";

// MOT system_group → VI section id mapping. A single system_group can map to
// multiple VI sections (e.g. body/seatbelts both live in "in_car_checks").
const MOT_GROUP_TO_SECTION = {
  brakes: ["brakes_wheels"],
  suspension: ["suspension_steering"],
  steering: ["suspension_steering"],
  tyres: ["tyres"],
  exhaust: ["exhaust_underside"],
  emissions: ["exhaust_underside", "fuel_system"],
  lights: ["lights_electrical"],
  visibility: ["in_car_checks", "lights_electrical"],
  washers_wipers: ["in_car_checks"],
  seatbelts: ["in_car_checks"],
  registration_plates: ["final_checks"],
  body: ["in_car_checks"],            // bonnet / boot latch / structural
  fluids_leaks: ["under_bonnet"],     // engine oil leak / coolant leak / power steering fluid leak
  other: ["final_checks"],            // last-resort dumping ground for unmappable MOT defects
};


// Coloured segmented progress bar — one slice per status type.
// (#15 — at-a-glance section severity)
const StatusBreakdownBar = ({ breakdown, total }) => {
  if (!total || total === 0) {
    return <div className="h-2 bg-slate-200 rounded-full" />;
  }
  const segs = [
    { key: "dangerous", colour: "bg-red-700", count: breakdown?.dangerous || 0 },
    { key: "red", colour: "bg-red-500", count: breakdown?.red || 0 },
    { key: "amber", colour: "bg-amber-500", count: breakdown?.amber || 0 },
    { key: "blue", colour: "bg-blue-500", count: breakdown?.blue || 0 },
    { key: "green", colour: "bg-green-500", count: breakdown?.green || 0 },
  ].filter(s => s.count > 0);
  return (
    <div className="h-2 bg-slate-200 rounded-full overflow-hidden flex" data-testid="status-breakdown-bar">
      {segs.map(s => (
        <div
          key={s.key}
          className={`h-full ${s.colour}`}
          style={{ width: `${(s.count / total) * 100}%` }}
          title={`${s.count} ${s.key}`}
          data-testid={`status-seg-${s.key}`}
        />
      ))}
    </div>
  );
};

// VI duration display (#16). Returns "32m" / "1h 04m"
const formatDuration = (seconds) => {
  if (!seconds || seconds < 0) return null;
  const mins = Math.floor(seconds / 60);
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h}h ${String(m).padStart(2, "0")}m`;
};

const SectionsListPage = () => {
  const { id } = useParams();
  const [sectionsData, setSectionsData] = useState(null);
  const [inspection, setInspection] = useState(null);
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [motRecurring, setMotRecurring] = useState([]); // [{system_group, location, occurrences, examples}]
  // MOT-Outcome → VI bridge: defects from the CURRENT-visit MOT outcome (if
  // submitted) grouped by VI section_id. Distinct from `motRecurring` which
  // tracks RECURRING advisories across previous DVSA tests.
  const [motVIFlags, setMotVIFlags] = useState({});
  // MOT outcome — pass/fail headline summary surfaced at the top of the VI
  // page so the tech doing the VI can see the MOT result at a glance
  // without opening the dedicated MOT workspace. Silent-fail.
  const [motOutcomeSummary, setMotOutcomeSummary] = useState(null);
  const navigate = useNavigate();

  // iter193 — Role-aware "Exit". FOH/ADMIN landing on this page via
  // FOHJobView's "Vehicle inspection" card used to be sent to
  // /app/tech/jobs (the technician dashboard). Route Exit to each
  // role's canonical home instead.
  const exitHref = useRoleHome();

  useEffect(() => {
    axios.get(`${API}/jobs/${id}`).then(jobRes => {
      setJob(jobRes.data);
    }).catch(() => {});
    
    // Pull MOT-recurring advisories for this job so we can highlight relevant
    // VI sections. Silent-fail — this is enrichment, not a blocker.
    axios.get(`${API}/jobs/${id}/mot-recurring-advisories`)
      .then(res => setMotRecurring(res.data?.groups || []))
      .catch(() => setMotRecurring([]));

    // MOT-Outcome → VI bridge — defects from the CURRENT-visit MOT outcome.
    // Silent-fail; the section list still renders without it.
    axios.get(`${API}/jobs/${id}/mot-vi-flags`)
      .then(res => setMotVIFlags(res.data?.by_section || {}))
      .catch(() => setMotVIFlags({}));

    // MOT outcome headline (pass/fail + counts) for the in-page summary
    // banner. Silent-fail. Returns ``{outcome:null, summary:{}}`` when no
    // MOT outcome has been submitted yet on this job.
    axios.get(`${API}/jobs/${id}/mot-outcome`)
      .then(res => setMotOutcomeSummary(
        res.data?.outcome ? (res.data?.summary || {}) : null
      ))
      .catch(() => setMotOutcomeSummary(null));
    
    axios.get(`${API}/jobs/${id}/inspection`).then(res => {
      if (!res.data) {
        navigate(`/app/tech/job/${id}/start`);
        return;
      }
      setInspection(res.data);
      axios.get(`${API}/inspections/${res.data.id}/sections`).then(sectRes => {
        setSectionsData(sectRes.data);
      });
    }).finally(() => setLoading(false));
  }, [id, navigate]);

  // Aggregate MOT recurring groups by VI section id → {count, highest_occ, examples}
  // Groups are sourced from the last 2 MOTs only (backend filter) and each
  // carries an `appears_on` label ("last" | "previous" | "both") so the tech
  // knows which MOT the advisory was raised on. Corrosion-flagged groups
  // ALSO surface on the exhaust_underside section so the underbody
  // corrosion check picks them up.
  const motHintsBySection = useMemo(() => {
    const out = {};
    (motRecurring || []).forEach(g => {
      const baseSections = MOT_GROUP_TO_SECTION[g.system_group] || [];
      const corrosionExtra = g.is_corrosion ? ["exhaust_underside"] : [];
      const sectionIds = [...new Set([...baseSections, ...corrosionExtra])];
      sectionIds.forEach(sid => {
        if (!out[sid]) out[sid] = { count: 0, maxOcc: 0, items: [], hasBoth: false, hasLast: false, hasPrevious: false };
        out[sid].count += 1;
        out[sid].maxOcc = Math.max(out[sid].maxOcc, g.occurrences || 0);
        if (g.appears_on === "both") out[sid].hasBoth = true;
        if (g.appears_on === "last") out[sid].hasLast = true;
        if (g.appears_on === "previous") out[sid].hasPrevious = true;
        out[sid].items.push({
          system_group: g.system_group,
          location: g.location,
          occurrences: g.occurrences,
          appears_on: g.appears_on || null,
          appears_on_text: g.appears_on_text || "",
          is_corrosion: !!g.is_corrosion,
          sample: g.examples?.[0]?.text || "",
        });
      });
    });
    return out;
  }, [motRecurring]);

  const handleExit = () => {
    navigate(exitHref);
  };

  if (loading) return <div className="p-8 text-center">Loading...</div>;

  // Guard: avoid flashing the "Arrival Photos Required" gate while the inner
  // sections fetch is still pending (sectionsData is still null at that point).
  // Without this, returning from SectionItemsPage after marking a section
  // complete shows the arrival-photos screen for a few hundred ms before the
  // real section list renders.
  if (!sectionsData) return <div className="p-8 text-center">Loading...</div>;

  if (!sectionsData?.arrival_complete) {
    return (
      <div className="min-h-screen bg-gray-100">
        <Header />
        <div className="p-4 max-w-md mx-auto text-center">
          <Card>
            <CardContent className="p-6">
              <Lock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h2 className="text-xl font-semibold">Arrival Photos Required</h2>
              <p className="text-gray-600 mt-2">Please capture all 4 arrival photos first.</p>
              <Button className="mt-4" onClick={() => navigate(`/app/tech/job/${id}/arrival`)} data-testid="go-to-arrival-btn">
                Go to Arrival Photos
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const allComplete = sectionsData.sections.every(s => s.is_complete);
  const completedSections = sectionsData.sections.filter(s => s.is_complete).length;
  const totalSections = sectionsData.sections.length;
  
  // Aggregate overall status breakdown across sections
  const overallBreakdown = sectionsData.sections.reduce((acc, s) => {
    const bd = s.status_breakdown || {};
    acc.green += bd.green || 0;
    acc.amber += bd.amber || 0;
    acc.red += bd.red || 0;
    acc.blue += bd.blue || 0;
    acc.dangerous += bd.dangerous || 0;
    return acc;
  }, { green: 0, amber: 0, red: 0, blue: 0, dangerous: 0 });
  const totalCompletedItems = sectionsData.sections.reduce((a, s) => a + s.completed_items, 0);
  
  const durationLabel = formatDuration(sectionsData.vi_duration_seconds);

  return (
    <div className="min-h-screen bg-gray-100">
      <Header />
      <div className="p-4 max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <Button 
            variant="outline" 
            size="sm"
            onClick={handleExit}
            className="flex items-center gap-2"
            data-testid="exit-inspection-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Exit & Save Progress
          </Button>
          {job && (
            <div className="text-right">
              <p className="font-semibold text-slate-800">{job.reg}</p>
              <p className="text-xs text-slate-500">{job.make} {job.model}</p>
            </div>
          )}
        </div>
        
        {/* Top-level progress + overall breakdown */}
        <div className="bg-white rounded-lg p-4 mb-4 border">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-semibold text-slate-800" data-testid="sections-list-title">Vehicle Inspection</h2>
            <span className="text-sm text-slate-600">{completedSections}/{totalSections} sections</span>
          </div>
          {/* Coloured stack of completed items (#15) */}
          <StatusBreakdownBar breakdown={overallBreakdown} total={totalCompletedItems} />
          <div className="flex items-center justify-between mt-2">
            <p className="text-xs text-slate-500 flex items-center gap-1">
              <Save className="w-3 h-3" />
              Progress auto-saved
            </p>
            {durationLabel && (
              <p className="text-xs text-slate-500 flex items-center gap-1" data-testid="vi-duration-stat">
                <Clock className="w-3 h-3" />
                {durationLabel} on this VI
              </p>
            )}
          </div>
        </div>

        {/* MOT Awareness — Tester mode. Surfaces only when there's MOT
            continuity to show (recurring advisories OR a linked intake
            session). Read-only; tester inspects independently. */}
        {(motRecurring.length > 0 || job?.intake_session_id) && (
          <>
            <MOTAwarenessPanel
              jobId={id}
              mode="tester"
              className="mb-2"
            />
            <MOTResultStripOrLink
              jobId={id}
              summary={motOutcomeSummary}
              onOpen={() => navigate(`/app/tech/mot/${id}`)}
            />
          </>
        )}
        
        <div className="space-y-3 mb-6">
          {sectionsData.sections.map((section) => {
            const motHint = motHintsBySection[section.id];
            const motFlag = motVIFlags[section.id];
            return (
            <Card 
              key={section.id}
              className={`${section.is_locked ? "opacity-60" : "cursor-pointer hover:shadow-md"} ${motFlag ? "ring-2 ring-teal-400 ring-offset-1" : motHint ? "ring-2 ring-purple-400 ring-offset-1" : ""} transition-all`}
              onClick={() => !section.is_locked && navigate(`/app/tech/job/${id}/section/${section.id}`)}
              data-testid={`section-card-${section.id}`}
            >
              <CardContent className="p-4">
                <div className="flex justify-between items-center mb-2">
                  <div className="flex items-center gap-3">
                    {section.is_locked ? (
                      <Lock className="w-5 h-5 text-gray-400" />
                    ) : section.is_complete ? (
                      <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                    ) : (
                      <div className="w-6 h-6 rounded-full border-2 border-gray-300" />
                    )}
                    <div>
                      <p className="font-semibold">{section.name}</p>
                      <p className="text-sm text-gray-600">
                        {section.completed_items}/{section.total_items} items
                      </p>
                    </div>
                  </div>
                  {!section.is_locked && <ChevronRight className="w-5 h-5 text-gray-400" />}
                </div>
                {/* MOT-Outcome → VI bridge — defects from the CURRENT-visit
                    MOT outcome surface here as a calm context block.
                    Tester wording preserved verbatim. Tech still
                    independently inspects + records. */}
                {motFlag && (
                  <div
                    className="mb-2 flex items-start gap-2 bg-teal-50 border border-teal-200 rounded p-2 text-xs text-teal-900"
                    data-testid={`mot-vi-flag-${section.id}`}
                  >
                    <ShieldCheck className="w-3.5 h-3.5 text-teal-700 mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1 flex-wrap">
                        <span className="font-semibold">From this MOT</span>
                        {motFlag.counts.terminal > 0 && (
                          <span className="ml-1 text-red-800 font-semibold">
                            · {motFlag.counts.terminal} {motFlag.counts.terminal === 1 ? "failure" : "failures"}
                          </span>
                        )}
                        {motFlag.counts.advisory > 0 && (
                          <span className="ml-1 text-teal-700">
                            · {motFlag.counts.advisory} {motFlag.counts.advisory === 1 ? "advisory" : "advisories"}
                          </span>
                        )}
                      </div>
                      <ul className="mt-0.5 space-y-0.5">
                        {motFlag.defects.slice(0, 3).map((f, idx) => (
                          <li
                            key={idx}
                            className="leading-snug"
                            data-testid={`mot-vi-flag-${section.id}-defect-${idx}`}
                          >
                            <span className={`font-semibold uppercase text-[10px] tracking-wide mr-1 ${
                              f.is_terminal ? "text-red-800" : "text-teal-700"
                            }`}>
                              {f.severity_label}
                            </span>
                            <span className="text-teal-900">{f.text}</span>
                          </li>
                        ))}
                        {motFlag.defects.length > 3 && (
                          <li className="text-teal-600 italic">
                            +{motFlag.defects.length - 3} more on the MOT workspace
                          </li>
                        )}
                      </ul>
                      <p className="mt-1 text-[10.5px] italic text-teal-700">
                        Context only — confirm independently before recording your findings.
                      </p>
                    </div>
                  </div>
                )}
                {/* MOT-recurring hint — last 2 MOTs only, labelled per test
                    iter198ss — Header wording changed because users were
                    reading "(last 2 MOTs)" as the CLAIM that each
                    advisory appeared on BOTH of the last 2 MOTs.
                    Reality: it's just the search window. Per-item
                    pills (LAST MOT / PREVIOUS MOT / BOTH) carry the
                    actual provenance for each line. */}
                {motHint && (
                  <div
                    className="mb-2 flex items-start gap-2 bg-purple-50 border border-purple-200 rounded p-2 text-xs text-purple-900"
                    data-testid={`mot-hint-${section.id}`}
                  >
                    <History className="w-3.5 h-3.5 text-purple-600 mt-0.5 shrink-0" />
                    <div className="flex-1">
                      <span className="font-semibold">
                        MOT advisor{motHint.count > 1 ? "ies" : "y"}
                      </span>
                      <span
                        className="ml-1 text-purple-700"
                        title="Yestimate searches the last two MOT tests. Each item below carries a pill showing which test it appeared on (LAST MOT, PREVIOUS MOT, or BOTH)."
                      >
                        · searched last 2 MOT tests
                      </span>
                      <ul className="mt-0.5 space-y-0.5">
                        {motHint.items.slice(0, 2).map((it, idx) => (
                          <li
                            key={idx}
                            className="text-purple-700"
                            data-testid={`mot-hint-item-${section.id}-${idx}`}
                          >
                            ·{" "}
                            {it.appears_on_text && (
                              <span
                                className={`inline-block mr-1 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ${
                                  it.appears_on === "both"
                                    ? "bg-purple-700 text-white"
                                    : it.appears_on === "last"
                                    ? "bg-purple-200 text-purple-900"
                                    : "bg-purple-100 text-purple-700"
                                }`}
                                title={
                                  it.appears_on === "both"
                                    ? "Recorded on both of the last two MOT tests"
                                    : it.appears_on === "last"
                                    ? "Recorded on the most recent MOT only — the previous test did NOT carry this advisory"
                                    : it.appears_on === "previous"
                                    ? "Recorded on the test before the most recent — the most recent MOT did NOT carry this advisory"
                                    : ""
                                }
                                data-testid={`mot-hint-appears-on-${section.id}-${idx}`}
                              >
                                {it.appears_on_text}
                              </span>
                            )}
                            {it.is_corrosion && (
                              <span
                                className="inline-block mr-1 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-orange-200 text-orange-900"
                                data-testid={`mot-hint-corrosion-${section.id}-${idx}`}
                              >
                                Corrosion
                              </span>
                            )}
                            <span className="capitalize">
                              {(it.location || "").replace(/_/g, " ")}{" "}
                              {(it.system_group || "")
                                .replace(/_/g, "/")
                                .replace(/\b\w/g, (c) => c.toUpperCase())}
                            </span>
                            {it.sample && (
                              <span className="text-purple-500"> – {it.sample.slice(0, 60)}</span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
                {/* Per-section coloured breakdown bar (#15) */}
                <StatusBreakdownBar
                  breakdown={section.status_breakdown}
                  total={section.completed_items}
                />
              </CardContent>
            </Card>
            );
          })}
        </div>

        <Button
          className="w-full"
          disabled={!allComplete}
          onClick={() => navigate(`/app/tech/job/${id}/submit`)}
          data-testid="proceed-to-submit-btn"
        >
          {allComplete ? "Review & Submit" : "Complete all sections to submit"}
        </Button>
      </div>
    </div>
  );
};

export default SectionsListPage;


// ---------------------------------------------------------------------------
// MOTResultStripOrLink — calm at-a-glance MOT outcome banner.
//
//   * When no outcome submitted yet  -> teal "Record this vehicle's MOT
//                                       outcome on the dedicated MOT workspace"
//                                       prompt + "Open MOT workspace" button.
//   * When PASSED                    -> emerald "MOT Complete · PASSED" headline
//                                       with expiry / mileage / advisory count.
//   * When FAILED                    -> red "MOT Complete · FAILED" headline
//                                       with dangerous/major defect counts.
//
// Tone: read-only summary — the workshop tech still opens the MOT
// workspace for any actioning. The full defect list lives there.
// ---------------------------------------------------------------------------
const MOTResultStripOrLink = ({ jobId, summary, onOpen }) => {
  if (!summary) {
    return (
      <div
        className="mb-4 rounded-md border border-teal-200 bg-teal-50/50 px-4 py-2.5 flex items-center justify-between gap-3"
        data-testid="mot-workspace-link-strip"
      >
        <p className="text-[13.5px] text-teal-900 leading-snug">
          Record this vehicle&apos;s MOT outcome on the dedicated MOT workspace.
        </p>
        <Button
          size="sm"
          variant="outline"
          onClick={onOpen}
          className="text-[13px] border-teal-300 text-teal-800 hover:bg-teal-100 flex-shrink-0"
          data-testid="open-mot-workspace-btn"
        >
          Open MOT workspace
        </Button>
      </div>
    );
  }

  const isPass = summary.test_result === "PASSED";
  const counts = summary.counts || {};
  const dangerous = counts.dangerous || 0;
  const major = counts.major || 0;
  const minor = counts.minor || 0;
  const advisory = counts.advisory || 0;
  const expiry = summary.mot_expiry_date
    ? new Date(summary.mot_expiry_date).toLocaleDateString("en-GB", {
        day: "numeric", month: "short", year: "numeric",
      })
    : null;
  const odo = summary.odometer_reading;
  const unit = summary.odometer_unit || "mi";

  const palette = isPass
    ? {
        bg:       "bg-emerald-50",
        ring:     "border-emerald-300",
        head:     "text-emerald-800",
        dot:      "bg-emerald-600",
        pill:     "text-emerald-800 bg-emerald-100 border-emerald-200",
        btn:      "border-emerald-300 text-emerald-800 hover:bg-emerald-100",
        headline: "MOT Complete · PASSED",
      }
    : {
        bg:       "bg-red-50/70",
        ring:     "border-red-300",
        head:     "text-red-800",
        dot:      "bg-red-600",
        pill:     "text-red-800 bg-red-100 border-red-200",
        btn:      "border-red-300 text-red-800 hover:bg-red-100",
        headline: "MOT Complete · FAILED",
      };

  return (
    <div
      className={`mb-4 rounded-md border ${palette.ring} ${palette.bg} px-4 py-3`}
      data-testid="mot-result-summary-banner"
      data-mot-result={summary.test_result}
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex items-center gap-2.5">
          <span className={`inline-block w-2 h-2 rounded-full ${palette.dot}`} aria-hidden="true" />
          <p
            className={`text-[14px] font-semibold ${palette.head} leading-snug`}
            data-testid="mot-result-summary-headline"
          >
            {palette.headline}
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={onOpen}
          className={`text-[12.5px] ${palette.btn} flex-shrink-0`}
          data-testid="open-mot-workspace-btn"
        >
          Open MOT workspace
        </Button>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        {isPass && expiry && (
          <span
            className={`text-[11.5px] px-2 py-0.5 rounded-full border font-semibold ${palette.pill}`}
            data-testid="mot-result-summary-expiry"
          >
            Expires {expiry}
          </span>
        )}
        {!isPass && dangerous > 0 && (
          <span
            className={`text-[11.5px] px-2 py-0.5 rounded-full border font-semibold ${palette.pill}`}
            data-testid="mot-result-summary-dangerous"
          >
            {dangerous} dangerous
          </span>
        )}
        {!isPass && major > 0 && (
          <span
            className={`text-[11.5px] px-2 py-0.5 rounded-full border font-semibold ${palette.pill}`}
            data-testid="mot-result-summary-major"
          >
            {major} major
          </span>
        )}
        {minor > 0 && (
          <span
            className="text-[11.5px] px-2 py-0.5 rounded-full border border-amber-200 bg-amber-50 text-amber-800 font-semibold"
            data-testid="mot-result-summary-minor"
          >
            {minor} minor
          </span>
        )}
        {advisory > 0 && (
          <span
            className="text-[11.5px] px-2 py-0.5 rounded-full border border-slate-200 bg-white text-slate-700 font-semibold"
            data-testid="mot-result-summary-advisory"
          >
            {advisory} advisory
          </span>
        )}
        {odo && (
          <span className="text-[11.5px] text-slate-500 ml-1" data-testid="mot-result-summary-odometer">
            at {Number(odo).toLocaleString()} {unit}
          </span>
        )}
      </div>
    </div>
  );
};

